/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { Avatar, Box, Button, Hidden, Paper, Snackbar, Typography } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBuyerDetails,
  addPrimaryContact,
  removePrimaryContact,
  fetchPrimaryContact,
} from 'aurora-composition/modules/primaryContact/actions';
import Loading from 'aurora-components/Loading';
import { AuroraButton, ADGMImage } from 'aurora-components';
import { keyEmptyCheck } from 'aurora-utils/utils';
import classNames from 'classnames';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import UserProfile from 'aurora-images/user_profile.svg';
import history from 'aurora-utils/history';
import NoPrimaryContact from './NoPrimaryContact';
import styles from './styles';
import Contact from './Contact';
import AddContactModal from './AddContactModal';
import ContactFields from './ContactFields';
import RemoveContactModal from './RemoveContactModal';

const PAGE_SIZE = 100;
function PrimaryContact(props) {
  const { classes, handleChange, form, ownerships, topAvatar } = props;
  const {
    location: { search },
  } = history;
  const query = qs.parse(search);
  const { page = 0 } = query;
  const payload = { page, PAGE_SIZE };
  const location = useLocation();

  const [modalConfig, setModalConfig] = useState({ open: false });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [removeModalConfig, setRemoveModalConfig] = useState({ open: false });

  const [removedDone, setRemovedDone] = useState({ show: false });
  const [updatedDone, setUpdatedDone] = useState({ show: false });

  const { loading, error, contacts } = useSelector(state => state.primaryContactReducer);

  const [showMoreContacts, setShowMoreContacts] = useState(false);

  const [selected, setSelected] = useState();

  const dispatch = useDispatch();

  const profileMode = useMemo(() => {
    return location.pathname === '/app/user/primary-contact' && true;
  }, []);

  const sortedContacts = useMemo(() => {
    return contacts?.sort(contact =>
      form?.primaryContact?.id
        ? (form?.primaryContact?.id === contact?.id) === true
          ? -1
          : 1
        : contact?.isDefault === true
        ? -1
        : 1,
    );
  }, [contacts, form?.primaryContact?.id]);

  useEffect(() => {
    error?.message && setShowSnackbar(true);
  }, [error]);

  useEffect(() => {
    if (profileMode) contacts && setSelected(contacts.filter(contact => contact.isDefault === true)[0] || null);
  }, [contacts]);

  function handleModalOpen() {
    setModalConfig({ ...modalConfig, open: true });
  }

  function handleModalClose() {
    setModalConfig({ open: false, data: null });
    removedDone.show && setRemovedDone({ show: false });
  }

  function handleFetchBuyer(id, userType) {
    dispatch(fetchBuyerDetails(id, userType)).then(res => {
      if (res?.buyer?.success) {
        setModalConfig({ ...modalConfig, data: res?.buyer?.result });
      }
    });
  }

  function handleAddPrimaryContact(data) {
    dispatch(addPrimaryContact(data)).then(res => {
      handleModalClose();
      if (!res?.statusCode) {
        profileMode
          ? dispatch(fetchPrimaryContact(payload))
          : dispatch(fetchPrimaryContact(null, form?.primaryLessorObj, form?.applicationId));
      }
    });
  }

  function handleRemovePrimaryContact(data) {
    dispatch(removePrimaryContact(data?.id)).then(res => {
      if (res) {
        setRemovedDone({ show: true, contact: data?.nameEn });
        dispatch(fetchPrimaryContact({ ...payload, applicationId: form?.applicationId }));
      }
    });
    setRemoveModalConfig({ open: false });
  }

  const onChange = (event, value) => {
    handleChange && handleChange(event.target.name, value);
  };

  useEffect(() => {
    if (!profileMode && !isEmpty(contacts)) {
      const defaultPrimaryContact =
        contacts?.filter(contact => contact?.isDefault === true).length > 1
          ? null
          : contacts?.filter(contact => contact?.isDefault === true)[0];
      form?.primaryContact?.id && !isEmpty(contacts?.filter(contact => contact.id === form?.primaryContact?.id))
        ? setSelected(form?.primaryContact)
        : setSelected(
            contacts?.filter(contact => contact.id === form?.primaryContactChosen?.id)[0] || defaultPrimaryContact,
          );
    }
  }, [contacts.length, form?.primaryContactChosen, form?.primaryLessorObj?.ownerID, form?.primaryLessorObj?.ownerId]);

  useEffect(() => {
    //
    if (!profileMode) {
      let contactPayload;
      if (form?.lessorIsPrimaryContact) {
        contactPayload = { lessorIsPrimaryContact: true };
      } else {
        contactPayload = selected?.id
          ? {
              id: selected?.id,
              type: selected?.type,
              lessorIsPrimaryContact: false,
            }
          : null;
      }
      handleChange('primaryContact', contactPayload);
    }
  }, [selected?.id, form?.lessorIsPrimaryContact, form?.primaryContactChosen]);
  useEffect(() => {
    if (form?.lessorIsPrimaryContact) setSelected({});
  }, [form?.lessorIsPrimaryContact]);

  useEffect(() => {
    profileMode && dispatch(fetchPrimaryContact({ ...payload, applicationId: form?.applicationId }));
  }, [page]);

  useEffect(() => {
    !profileMode && dispatch(fetchPrimaryContact(null, form?.primaryLessorObj, form?.applicationId));
  }, [form?.primaryLessorObj?.ownerId]);

  function handleUpdateContact() {
    dispatch(addPrimaryContact({ id: selected?.id, isDefault: true })).then(res => {
      res && setUpdatedDone({ show: true, contact: selected?.nameEn });
    });
  }

  function DisabledLessorIsPrimaryContactCheckbox() {
    if (ownerships?.length === 1 && ownerships[0]?.ownerType === 1) return false;
    if (
      form?.primaryLessorObj?.ownerType === 2 ||
      form?.primaryLessorObj?.tradeLicenseNumber ||
      !form?.primaryLessorObj?.customerId
    )
      return true;
    return false;
  }

  return (
    <div className={profileMode && classes.contentArea}>
      {loading && <Loading />}
      {/* For showing error */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => {
            setShowSnackbar(false);
          }}
          severity='error'
          variant='filled'
          sx={{ width: '100%' }}
        >
          {keyEmptyCheck(error?.message)}
        </Alert>
      </Snackbar>
      {/* User Avatar */}
      {topAvatar && (
        <Hidden smUp>
          <Box display='flex' justifyContent='center' mb={3}>
            <Avatar alt='Avatar alt' src={UserProfile} className={classes.userAvatar} />
          </Box>
        </Hidden>
      )}

      {profileMode && isEmpty(contacts) && (
        <div className={classes.title_container}>
          <Typography variant='h5' component='h1' className={classes.title}>
            {keyEmptyCheck('primary_contact_details')}
          </Typography>
        </div>
      )}

      {/* show removing success msg */}
      {removedDone?.show && !isEmpty(contacts) && (
        <Box display='flex' alignItems='center' px={1} mb={2} mx='auto' className={classes.successMsg}>
          <ADGMImage name='success.svg' alt='successIcon' />
          <Typography>
            {keyEmptyCheck('primary_contact_removed_successfully', {
              s: <b>{removedDone.contact}</b>,
            })}
          </Typography>
        </Box>
      )}

      {/* show updated success msg */}
      {updatedDone?.show && !isEmpty(contacts) && (
        <Box display='flex' alignItems='center' px={1} mb={2} mx='auto' className={classes.successMsg}>
          <ADGMImage name='success.svg' alt='successIcon' />
          <Typography>
            {keyEmptyCheck('primary_contact_made_default_successfully', {
              s: <b>{updatedDone.contact}</b>,
            })}
          </Typography>
        </Box>
      )}

      <Paper
        // variant="outlined"
        mb={3}
        className={profileMode && classes.paperTypeBlock}
      >
        {!profileMode && isEmpty(contacts) && (
          <Box display='flex' flexDirection='column' className={classes.cardContent}>
            <div className={classNames(classes.titleHead, classes.noTitleHeadXPaddingOnSm)}>
              <Typography variant='h6' component='h1' className={classes.title}>
                {keyEmptyCheck('primary_contact_details')}
                <span className={classes.subTitle1}>*</span>
              </Typography>
            </div>
            <Box
              display='flex'
              justifyContent='space-between'
              flexDirection='column'
              className={classes.contactsContainer}
            >
              {!profileMode && (
                <div className={classes.checkboxContainer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form?.lessorIsPrimaryContact}
                        onChange={onChange}
                        name='lessorIsPrimaryContact'
                        classes={{ root: classes.checkbox }}
                        disabled={DisabledLessorIsPrimaryContactCheckbox()}
                      />
                    }
                    label={keyEmptyCheck('primary_lessor_same_as_primary_contact')}
                  />
                </div>
              )}
            </Box>
          </Box>
        )}

        {!isEmpty(contacts) && (
          <Box display='flex' flexDirection='column' className={classes.cardContent}>
            <div className={classes.headerWraperComponent}>
              <div className={classNames(classes.titleHead, classes.noTitleHeadXPaddingOnSm)}>
                <Typography variant='h6' component='h1' className={classes.title}>
                  {keyEmptyCheck('primary_contact_details')}
                  {!profileMode && <span className={classes.subTitle1}>*</span>}
                </Typography>
              </div>

              <Button
                variant='outlined'
                className={classNames(
                  classes.largeButton,
                  classes.addNewContactBtn,
                  !profileMode && classes.addNewContactBtnLeft,
                )}
                size='large'
                onClick={handleModalOpen}
              >
                <svg width='17' height='17' viewBox='0 0 17 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M8.80566 2V15.6111'
                    stroke='#000'
                    strokeWidth='2.72222'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M2 8.80566H15.6111'
                    stroke='#000'
                    strokeWidth='2.72222'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>

                <Typography component='p' variant='body1'>
                  {keyEmptyCheck('add_new_contact_details')}
                </Typography>
              </Button>
            </div>
            <Box
              display='flex'
              justifyContent='space-between'
              flexDirection='column'
              className={classes.contactsContainer}
            >
              {!profileMode && (
                <div className={classes.checkboxContainer}>
                  <FormControlLabel
                    // value={form?.lessorIsPrimaryContact}
                    control={
                      <Checkbox
                        checked={!!form?.lessorIsPrimaryContact}
                        onChange={onChange}
                        name='lessorIsPrimaryContact'
                        classes={{ root: classes.checkbox }}
                        disabled={DisabledLessorIsPrimaryContactCheckbox()}
                      />
                    }
                    label={keyEmptyCheck('primary_lessor_same_as_primary_contact')}
                  />
                </div>
              )}
              {sortedContacts?.map((contact, index) => (
                <div
                  style={{
                    display: !showMoreContacts && index >= 2 ? 'none' : 'inherit',
                  }}
                >
                  <Contact
                    index={index}
                    contact={contact}
                    selected={form?.lessorIsPrimaryContact ? null : selected}
                    checksDisabled={form?.lessorIsPrimaryContact}
                    setSelected={setSelected}
                    setRemoveModalConfig={setRemoveModalConfig}
                    classes={classes}
                    profileMode={profileMode}
                  />
                </div>
              ))}
              {!showMoreContacts && contacts.length > 2 && (
                <Box
                  onClick={() => setShowMoreContacts(!showMoreContacts)}
                  display='flex'
                  justifyContent='center'
                  className={classes.showMore}
                  py={2}
                  mx='auto'
                >
                  {keyEmptyCheck('view_more_contacts', {
                    s: `${contacts.length - 2}`,
                  })}
                  <span className={classes.arrowIcon}>
                    <ADGMImage width='18px' name='arrow-down-orange.svg' alt='arrowIcon' />
                  </span>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Case No primary contact exist  */}
        {!loading && isEmpty(contacts) && (
          <div className={classNames(classes.cardContent)}>
            <NoPrimaryContact
              classes={classes}
              handleFetchBuyer={handleFetchBuyer}
              handleAddPrimaryContact={handleAddPrimaryContact}
              handleModalOpen={handleModalOpen}
              handleModalClose={handleModalClose}
              modalConfig={modalConfig}
              profileMode={profileMode}
            />
          </div>
        )}
      </Paper>

      {profileMode && !isEmpty(contacts) && (
        <Box
          component='Paper'
          className={classNames(classes.saveBtnContainer)}
          mt={5}
          px={3}
          display='flex'
          justifyContent='flex-end'
        >
          <AuroraButton
            onClick={handleUpdateContact}
            disabled={!selected}
            variant='primary'
            size='large'
            color='primary'
          >
            {keyEmptyCheck('save')}
          </AuroraButton>
        </Box>
      )}

      {/* ADD Contact MODAL */}
      <AddContactModal
        modalConfig={modalConfig}
        handleModalClose={handleModalClose}
        classes={classes}
        handleFetchUser={handleFetchBuyer}
        modalTitle={keyEmptyCheck('new_contact_details')}
        buttonText={keyEmptyCheck('submit')}
        handleAddPrimaryContact={handleAddPrimaryContact}
        FieldComponent={ContactFields}
      />

      {/* Remove Contact MODAL */}
      {profileMode && (
        <RemoveContactModal
          modalConfig={removeModalConfig}
          handleModalClose={() => setRemoveModalConfig({ open: false })}
          classes={classes}
          handleFetchUser={handleFetchBuyer}
          buttonText={keyEmptyCheck('remove')}
          handleRemovePrimaryContact={handleRemovePrimaryContact}
        />
      )}
    </div>
  );
}
PrimaryContact.propTypes = {
  classes: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  ownerships: PropTypes.array,
  topAvatar: PropTypes.bool,
};

PrimaryContact.defaultProps = {
  ownerships: [],
  topAvatar: true,
};
export default withStyles(styles)(PrimaryContact);
